import React, { Component, ReactElement } from 'react'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import cn from 'classnames'
import { Button, TextField, FormControl } from '@material-ui/core'
import { debounce, DebouncedFunc, isEmpty, isEqual } from 'lodash'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { onFormInputEnterPress } from 'helpers/input'
import { KEYBOARD_KEYS } from 'helpers/keyboardKeys'
import { NUMBER_REG_EXP_PATTERN } from 'constants/regExps'
import {
  TYPE_INPUT_CALCULATED,
  TYPE_INPUT_COUNTER,
  TYPE_INPUT_NUMBER,
} from 'helpers/jobs'
import {
  JobNumberInputProps,
  JobNumberInputState,
  Input,
  Licensee,
  NumberInputValue,
} from 'routes/App/Apps/MyJobs/JobFields/JobFields.types'
import messages from '../translate'
import { ErrorValidationMessage } from './JobFields.styles'

export class JobNumberInput extends Component<
  JobNumberInputProps,
  JobNumberInputState
> {
  public debouncedInputBlur: DebouncedFunc<() => void>

  constructor(props: JobNumberInputProps) {
    super(props)

    this.debouncedInputBlur = debounce(this.onInputBlur.bind(this), 1000)
    this.state = {
      value: null,
      oldValue: null,
      evtTaskInputId: '',
      notificationMessage: '',
      isUpdating: false,
    }
  }

  componentDidMount() {
    const {
      inputData: { value = null, evtInputId },
    } = this.props

    this.setInputValue(value, evtInputId)
  }

  shouldComponentUpdate(nextProps: JobNumberInputProps) {
    const { inputInfo, inputData } = this.props
    const { inputInfo: nextInputInfo, inputData: nextInputData } = nextProps

    if (
      !isEqual(inputInfo, nextInputInfo) ||
      !isEqual(inputData, nextInputData)
    ) {
      const { value = null, evtInputId } = nextInputData

      this.checkValueRange(value)
      this.setInputValue(value, evtInputId)

      return false
    }

    return true
  }

  componentDidUpdate(
    prevProps: JobNumberInputProps,
    prevState: JobNumberInputState,
  ) {
    const { oldValue: prevOldValue } = prevState
    const {
      index: inputId,
      inputInfo: { parameters: { lookup_table_source } = {} },
      inputData: { value },
      dependency: { currentEvtTask, evtTaskMatches },
      inputs,
      activeJobId,
      handleDependentLookupInputs,
    } = this.props

    if (
      lookup_table_source &&
      (!lookup_table_source?.parents ||
        lookup_table_source?.parents?.length === 1) &&
      (!value || value !== prevOldValue)
    ) {
      const matchCalculationFields = inputs.filter(
        ({ type, parameters: { formula } = {} }: Input) =>
          type === TYPE_INPUT_CALCULATED &&
          formula &&
          formula.match(`${inputId}`),
      )

      handleDependentLookupInputs(
        inputs,
        evtTaskMatches || [currentEvtTask],
        inputId,
        activeJobId,
        matchCalculationFields,
      )
    }
  }

  setInputValue = (value: NumberInputValue, evtTaskInputId: number) => {
    this.setState({
      value,
      oldValue: value,
      evtTaskInputId,
    })
  }

  setResultId = ({ id }: { id: number }): void => {
    this.setState({ evtTaskInputId: id })
  }

  formatNumber = (
    value: string | number,
    typeNumber?: string,
  ): string | number => {
    const {
      licensee: { latinAmericanNumberFormat },
    } = this.props
    let formattedValue = value

    // if (inputInfo?.parameters?.decimals) {
    //   if (latinAmericanNumberFormat) {
    //     if (!typeNumber && typeof value === 'number') {
    //       formattedValue = value.toString().replace(/[.]/g, ',')
    //     }
    //   } else if (typeof value === 'string' && value.includes(',')) {
    //     formattedValue = Number(value.replace(/[,]/g, '.'))
    //   }
    // } else {
    //   formattedValue = String(value).replace(/[,.]/g, '')
    // }

    if (latinAmericanNumberFormat) {
      if (!typeNumber && typeof value === 'number') {
        formattedValue = value.toString().replace(/[.]/g, ',')
      }
    } else if (typeof value === 'string' && value.includes(',')) {
      formattedValue = Number(value.replace(/[,]/g, '.'))
    }

    return formattedValue
  }

  onInputChange = ({
    target: { value: targetValue },
  }: React.ChangeEvent<{ value: string }>): void => {
    const numberRegExp = new RegExp(NUMBER_REG_EXP_PATTERN)
    let value = targetValue.trim()

    if (/^[-+]\.$/.test(value)) {
      value = `${value.substr(0, 1)}0.`
    }

    if (!numberRegExp.test(value)) {
      return
    }

    this.setState({ value })
  }

  checkValueRange = (value: NumberInputValue): void => {
    const {
      onJobTaskValueRange,
      intl: { formatMessage },
      inputInfo: { parameters: { range, restrictive } = {}, workflow_input },
      licensee: { id: licenseeId },
    } = this.props
    const hasRange = range && range.length > 0
    // TODO temporary hardcode for demo, it will remove after demo
    const calculationInputs = [
      295861,
      295859,
      295857,
      295845,
      295855,
      295853,
      295851,
      295838,
      295849,
      295841,
      295847,
      295843,
      295836,
      295839,
    ]
    const ids = [151506, 151512, 151513, 151514, 275425, 275431, 275432, 275433]
    const licensees = [2046, 10566]
    if (workflow_input && workflow_input === 296871) {
      let notificationMessage = ''
      if (typeof value === 'number' && value > 50) {
        notificationMessage = 'Mortality is high, please alert manager'
      }
      this.setState(() => ({ notificationMessage }))
    } else if (workflow_input && workflow_input === 296875) {
      let notificationMessage = ''
      if (typeof value === 'number' && value > 3000) {
        notificationMessage =
          'Water volume is high, please check to see if there is a leak'
      }
      this.setState(() => ({ notificationMessage }))
    } else if (workflow_input && calculationInputs.includes(workflow_input)) {
      let notificationMessage = ''
      if (typeof value === 'number' && value >= 10) {
        notificationMessage =
          'Excessive Usage, please check water lines for leaks!'
      }
      this.setState(() => ({ notificationMessage }))
    } else if (workflow_input && [282363, 282362].includes(workflow_input)) {
      let notificationMessage = ''
      if (typeof value === 'number' && value >= 10) {
        notificationMessage = 'Mortality is high, please alert management'
      }
      this.setState(() => ({ notificationMessage }))
    } else if (workflow_input && [287986, 289946].includes(workflow_input)) {
      let notificationMessage = ''
      if (typeof value === 'number' && value > 0) {
        notificationMessage = 'Resample required because of blood spot'
      }
      this.setState(() => ({ notificationMessage }))
    } else if (
      licensees.includes(licenseeId) &&
      workflow_input &&
      ids.includes(workflow_input)
    ) {
      let notificationMessage = ''
      if (typeof value === 'number' && value < 1600) {
        notificationMessage =
          'Warning: water consumption is more than 10% LOWER than the target for this age. Check birds health and waterlines!'
      } else if (typeof value === 'number' && value > 1800) {
        notificationMessage =
          'Warning: water consumption is more than 10% HIGHER than the target for this age. Check the waterlines, there might be a leakage!'
      }

      this.setState(() => ({ notificationMessage }))
    } /* TODO end */ else if (hasRange) {
      const [min, max] = range
      let notificationMessage = ''

      if (typeof value === 'number' && (value < min || value > max)) {
        notificationMessage = formatMessage(messages.usuallyTheRangeIs, {
          rangeLimits: `${min}-${max}`,
        })

        if (onJobTaskValueRange) {
          onJobTaskValueRange(restrictive)
        }
      } else {
        onJobTaskValueRange(false)
      }

      this.setState({ notificationMessage })
    }
  }

  onInputBlur = (): void => {
    const {
      inputInfo,
      onJobTaskAutoSave,
      inputData: { evtInputId },
      index,
      inputs,
      activeTasks,
      setIsDebouncing,
    } = this.props
    const { value: prevValue, oldValue, evtTaskInputId } = this.state
    const isCounter = inputInfo.type === TYPE_INPUT_COUNTER
    let value = prevValue
    // const digit = 10 ** (inputInfo?.parameters?.decimals || 0)
    const hasParameters =
      inputInfo &&
      inputInfo.parameters &&
      Object.prototype.hasOwnProperty.call(inputInfo.parameters, 'decimals')
    const digit =
      10 **
      (hasParameters
        ? (inputInfo.parameters && inputInfo.parameters.decimals) || 0
        : 2)
    if (typeof value === 'string' && value.includes(',')) {
      value = Number(value.replace(/[,.]/g, i => (i === ',' ? '.' : ',')))
    }

    if (value && !Number.isInteger(value)) {
      value = Number.isNaN(value) ? null : Math.round(+value * digit) / digit
    }

    if (isCounter && typeof value === 'number') {
      if (value < 0) {
        value = 0
      } else {
        value = Math.round(value)
      }
    }

    const formattedValue = value || value === 0 ? value : null

    const matchCalculationFields = inputs.filter(
      ({ type, parameters: { formula } = {} }: Input) =>
        type === TYPE_INPUT_CALCULATED && formula && formula.match(`${index}`),
    )

    if (value !== oldValue) {
      this.setState({ isUpdating: true })

      onJobTaskAutoSave(
        {
          evtInputId: evtInputId || evtTaskInputId,
          result: formattedValue,
          input: inputInfo.id,
          setResultId: this.setResultId,
          matchCalculationFields,
          activeTasks,
        },
        () => {
          this.setState({ isUpdating: false })
          setIsDebouncing(false)
        },
      )

      this.setState({ oldValue: value })
    } else {
      this.setState({ isUpdating: true })

      onJobTaskAutoSave(false, () => {
        this.setState({ isUpdating: false })
        setIsDebouncing(false)
      })
    }

    this.checkValueRange(value)
    this.setState({ value })
  }

  handleWheelChange = (event: React.WheelEvent<HTMLInputElement>): void => {
    const target = event.target as HTMLInputElement

    target.blur()
  }

  handleKeyDownChange = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (KEYBOARD_KEYS[event.key as keyof typeof KEYBOARD_KEYS]) {
      event.preventDefault()
    }
  }

  handleInputFocus = (): void => {
    const { onJobTaskFocusedInput } = this.props

    onJobTaskFocusedInput(true)
  }

  handleCounterChange = (value: number): void => {
    const { setIsDebouncing } = this.props
    const formattedValue = value < 0 ? 0 : value

    this.setState({ value: formattedValue }, () => {
      setIsDebouncing(true)
      this.debouncedInputBlur()
    })
  }

  counterButton = (value: number, action: string): ReactElement => {
    const { isUpdating } = this.state
    const { disabled } = this.props

    return (
      <Button
        disabled={disabled || isUpdating}
        color="primary"
        size="large"
        variant="contained"
        type="button"
        className={`counter-button ${action}`}
        onClick={() => {
          this.handleCounterChange(value)
        }}
      />
    )
  }

  render() {
    const {
      disabled,
      className,
      inputInfo: {
        title,
        required,
        type,
        parameters: { restrictive, editable } = {},
      },
      inputHint,
      errors,
      featureFlags,
      inputData: { evtInputId },
    } = this.props
    const { value, notificationMessage, isUpdating } = this.state
    const typeNumber = ifWindowWidthLessThan(TABLET_BREAKPOINT)
      ? TYPE_INPUT_NUMBER
      : undefined
    const checkValue =
      !value && value !== 0 ? '' : this.formatNumber(value, typeNumber)
    const drawAsterisk = required ? '*' : ''
    const isCounter = type === TYPE_INPUT_COUNTER
    const isInputDisabled = disabled || isUpdating
    let getFieldIcon

    switch (type) {
      case TYPE_INPUT_NUMBER: {
        getFieldIcon = <span className="job-field-icon">#</span>
        break
      }
      case TYPE_INPUT_CALCULATED: {
        getFieldIcon = <span className="job-field-icon">=</span>
        break
      }
      default: {
        getFieldIcon = null
      }
    }

    const isErrorValidation =
      featureFlags.NEGATIVE_LOOKUP_INVENTORY &&
      !isEmpty(errors) &&
      !!errors[evtInputId]

    return (
      <FormControl
        fullWidth
        className={cn(className, {
          alert: restrictive && notificationMessage,
          warning: !restrictive && notificationMessage,
        })}>
        <span className="job-field-label">
          {title} {drawAsterisk}
        </span>
        <div className="job-field-wrapper">
          {getFieldIcon}
          {isCounter && this.counterButton((+checkValue || 0) - 1, 'decrease')}
          <TextField
            value={checkValue}
            disabled={
              isInputDisabled || (typeof editable === 'boolean' && !editable)
            }
            type={typeNumber}
            onChange={this.onInputChange}
            onWheel={this.handleWheelChange}
            onKeyDown={this.handleKeyDownChange}
            className={cn('job-field-input-box', {
              error: isErrorValidation,
            })}
            inputProps={{
              className: cn('job-field-input', {
                'js-enter-press-input': !isInputDisabled,
                'is-counter-input': isCounter,
              }),
              onBlur: this.onInputBlur,
              onKeyUp: onFormInputEnterPress,
              onFocus: this.handleInputFocus,
            }}
          />
          {isCounter && this.counterButton((+checkValue || 0) + 1, 'increase')}
        </div>
        {inputHint}
        {notificationMessage && (
          <span className="job-field-alert-text">{notificationMessage}</span>
        )}
        {isErrorValidation && (
          <ErrorValidationMessage>{errors[evtInputId]}</ErrorValidationMessage>
        )}
      </FormControl>
    )
  }
}

export default connect(
  (state: {
    getIn: (path: Array<string>) => Licensee | Record<string, boolean>
  }) => ({
    licensee: state.getIn(['profile', 'profile', 'licensee']) as Licensee,
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']) as Record<
      string,
      boolean
    >,
  }),
)(injectIntl(JobNumberInput))
